import { create } from "zustand";
import type { User } from "firebase/auth";
import { toAppError, type AppError } from "@/data/errors";
import { subscribeSystemAdmin } from "@/data/firestore/systemRepository";

type State = {
  uid: string | null;
  adminAccessResolved: boolean;
  isSystemAdmin: boolean;
  error: AppError | null;
  start: (user: User | null) => void;
  stop: () => void;
};

let unsubscribe: (() => void) | undefined;
let generation = 0;
const initial = { uid: null, adminAccessResolved: false, isSystemAdmin: false, error: null };

export const useSystemStore = create<State>((set, get) => ({
  ...initial,
  start: (user) => {
    if (user?.uid === get().uid && (unsubscribe || get().adminAccessResolved)) return;
    const currentGeneration = ++generation;
    unsubscribe?.();
    unsubscribe = undefined;
    set({ ...initial, uid: user?.uid ?? null });
    if (!user || user.isAnonymous) {
      set({ adminAccessResolved: true, isSystemAdmin: false });
      return;
    }
    const fail = (error: AppError) => {
      if (generation === currentGeneration) set({ adminAccessResolved: true, isSystemAdmin: false, error });
    };
    try {
      unsubscribe = subscribeSystemAdmin(user.uid, {
        next: (isSystemAdmin) => {
          if (generation === currentGeneration) set({ adminAccessResolved: true, isSystemAdmin, error: null });
        },
        error: fail,
      });
    } catch (error) {
      fail(toAppError(error, "subscription-failed"));
    }
  },
  stop: () => {
    generation += 1;
    unsubscribe?.();
    unsubscribe = undefined;
    set(initial);
  },
}));
